import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, FileText, MapPin, Pause, Play } from 'lucide-react';
import { Avatar } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import {
  useAbrirConversa,
  useAgentes,
  useCanais,
  useMarcarLido,
  useMensagens,
  enviarMensagemStream,
} from '../api';
import { anexoSrc } from '../upload';
import type { Mensagem } from '../schemas';
import { ChatInput, type EnviarPayload } from './ChatInput';

const ROLES: Record<string, string> = {
  agronomia: 'consultor agronômico',
  credito: 'consultor de crédito',
  fundiario: 'consultor fundiário',
  ambiental: 'consultor ambiental',
};
function roleLabel(dominio?: string | null): string {
  return (dominio && ROLES[dominio]) || 'assistente';
}
function fmtHoraMsg(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// reconcilia mensagens por id (otimista do stream + polling do servidor), ordenado
function mergeMsgs(a: Mensagem[], b: Mensagem[]): Mensagem[] {
  const map = new Map<number, Mensagem>();
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  return [...map.values()].sort((x, y) => x.id - y.id);
}

export function ChatView({ agente, canal }: { agente: string; canal: string }) {
  const navigate = useNavigate();
  const semCanal = canal === 'direto';

  const { data: agentes } = useAgentes();
  const ag = (agentes ?? []).find((a) => a.id === agente);
  const { data: canais } = useCanais(agente, !semCanal);
  const canalNome = semCanal ? 'Conversa' : (canais ?? []).find((c) => c.slug === canal)?.nome ?? canal;

  const abrir = useAbrirConversa();
  const marcarLido = useMarcarLido();
  const [conversaId, setConversaId] = useState<number | null>(null);
  const [msgs, setMsgs] = useState<Mensagem[]>([]);
  const [streamText, setStreamText] = useState<string | null>(null); // resposta parcial
  const [sending, setSending] = useState(false);

  // abre (get-or-create) a conversa ao montar / trocar de canal
  useEffect(() => {
    setConversaId(null);
    setMsgs([]);
    abrir.mutate(
      { agenteId: agente, ...(semCanal ? {} : { canalSlug: canal }) },
      { onSuccess: (c) => setConversaId(c.id) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agente, canal]);

  const { data: page } = useMensagens(conversaId);
  useEffect(() => {
    if (page) setMsgs((prev) => mergeMsgs(prev, page.mensagens));
  }, [page]);
  // marca lido quando chega mensagem nova do agente (stream OU polling) e ao abrir
  const lastMarkedRef = useRef<number | null>(null);
  useEffect(() => {
    if (conversaId == null || msgs.length === 0) {
      lastMarkedRef.current = null;
      return;
    }
    const last = msgs[msgs.length - 1];
    if (last && last.papel !== 'user' && last.id !== lastMarkedRef.current) {
      lastMarkedRef.current = last.id;
      marcarLido.mutate(conversaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs, conversaId]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, streamText]);

  async function enviar(payload: EnviarPayload) {
    if (conversaId == null || sending) return;
    setSending(true);
    setStreamText('');
    let acc = '';
    await enviarMensagemStream(conversaId, payload, {
      onUserMsg: (m) => setMsgs((x) => mergeMsgs(x, [m])),
      onToken: (t) => {
        acc += t;
        setStreamText(acc);
      },
      onDone: (m) => {
        setMsgs((x) => mergeMsgs(x, [m]));
        setStreamText(null);
      },
      onPendente: () => setStreamText(null), // resposta aparece sozinha via polling
      onError: () => setStreamText(null),
    });
    setSending(false);
  }

  function voltar() {
    if (semCanal) void navigate({ to: '/agentes' });
    else void navigate({ to: '/agentes/$agente', params: { agente } });
  }

  return (
    <div className="flex flex-col h-full bg-bg">
      {/* header */}
      <div className="flex items-center gap-2.5 px-3 h-16 border-b border-border shrink-0 bg-bg">
        <button
          type="button"
          onClick={voltar}
          className="flex items-center justify-center size-9 rounded-full hover:bg-surface2 text-text shrink-0"
          aria-label="Voltar"
        >
          <ChevronLeft size={26} strokeWidth={2} />
        </button>
        <div className="relative shrink-0">
          <Avatar name={ag?.nome ?? agente} size="lg" {...(ag?.cor ? { color: ag.cor } : {})} />
          <span className="absolute bottom-0 right-0 size-2.5 rounded-full bg-[var(--ok)] ring-2 ring-bg" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-[0.9rem] font-semibold text-text truncate leading-tight">
            {ag?.nome ?? agente}
          </span>
          <span className="text-[0.74rem] text-text-2 truncate leading-tight">
            online · {semCanal ? roleLabel(ag?.dominio) : canalNome}
          </span>
        </div>
      </div>

      {/* mensagens */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 flex flex-col gap-1.5">
        {msgs.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        {streamText !== null &&
          (streamText ? (
            <Bubble
              m={{ papel: 'assistant', tipo: 'texto', corpo: streamText, anexo_url: null, anexo_meta: null, ts: '' }}
              streaming
            />
          ) : (
            <Linha isUser={false}>
              <div className="rounded-2xl rounded-bl-md bg-surface shadow-sm px-3.5 py-3">
                <TypingDots />
              </div>
            </Linha>
          ))}
        {msgs.length === 0 && streamText === null && (
          <p className="text-center text-[0.82rem] text-text-muted mt-8">
            Comece a conversa com {ag?.nome ?? 'o assistente'}.
          </p>
        )}
      </div>

      <ChatInput onEnviar={(p) => void enviar(p)} disabled={conversaId == null || sending} />
    </div>
  );
}

type BubbleMsg = Pick<Mensagem, 'papel' | 'tipo' | 'corpo' | 'anexo_url' | 'anexo_meta' | 'ts'>;

function Linha({ isUser, children }: { isUser: boolean; children: React.ReactNode }) {
  return <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>{children}</div>;
}

// indicador "digitando" — o agente real demora (consulta dados, raciocina)
function TypingDots() {
  return (
    <div className="flex items-center gap-1">
      <span className="size-1.5 animate-bounce rounded-full bg-text-muted" />
      <span className="size-1.5 animate-bounce rounded-full bg-text-muted [animation-delay:0.15s]" />
      <span className="size-1.5 animate-bounce rounded-full bg-text-muted [animation-delay:0.3s]" />
    </div>
  );
}

function Bubble({ m, streaming }: { m: BubbleMsg; streaming?: boolean }) {
  if (m.papel === 'system') {
    return <p className="text-center text-[0.76rem] text-danger my-1">{m.corpo}</p>;
  }
  const isUser = m.papel === 'user';
  const meta = (m.anexo_meta ?? {}) as {
    nome_original?: string;
    lat?: number;
    lon?: number;
    dur_ms?: number;
  };

  // foto
  if (m.tipo === 'foto' && m.anexo_url) {
    return (
      <Linha isUser={isUser}>
        <img
          src={anexoSrc(m.anexo_url)}
          alt={meta.nome_original ?? 'Imagem'}
          className="max-w-[70%] max-h-72 rounded-2xl border border-border object-cover"
        />
      </Linha>
    );
  }
  // áudio (player custom estilo Figma)
  if (m.tipo === 'audio' && m.anexo_url) {
    return (
      <Linha isUser={isUser}>
        <AudioBubble src={anexoSrc(m.anexo_url)} durMs={meta.dur_ms} isUser={isUser} />
      </Linha>
    );
  }
  // arquivo
  if (m.tipo === 'arquivo' && m.anexo_url) {
    return (
      <Linha isUser={isUser}>
        <a
          href={anexoSrc(m.anexo_url)}
          target="_blank"
          rel="noreferrer"
          className={cn(
            'flex items-center gap-2 max-w-[78%] px-3.5 py-2.5 rounded-2xl text-[0.85rem]',
            isUser ? 'bg-[var(--accent)] text-[var(--on-solid)]' : 'bg-surface text-text shadow-sm',
          )}
        >
          <FileText size={18} strokeWidth={1.5} className="shrink-0" />
          <span className="truncate">{meta.nome_original ?? 'Arquivo'}</span>
        </a>
      </Linha>
    );
  }
  // localização
  if (m.tipo === 'localizacao' && meta.lat != null && meta.lon != null) {
    const url = `https://www.openstreetmap.org/?mlat=${meta.lat}&mlon=${meta.lon}#map=16/${meta.lat}/${meta.lon}`;
    return (
      <Linha isUser={isUser}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={cn(
            'flex items-center gap-2 max-w-[78%] px-3.5 py-2.5 rounded-2xl text-[0.85rem]',
            isUser ? 'bg-[var(--accent)] text-[var(--on-solid)]' : 'bg-surface text-text shadow-sm',
          )}
        >
          <MapPin size={18} strokeWidth={1.5} className="shrink-0" />
          <span>Localização</span>
        </a>
      </Linha>
    );
  }

  // texto / comando
  return (
    <Linha isUser={isUser}>
      <div
        className={cn(
          'max-w-[78%] px-3.5 py-2 rounded-2xl text-[0.9rem] leading-snug',
          isUser
            ? 'bg-[var(--accent)] text-[var(--on-solid)] rounded-br-md'
            : 'bg-surface text-text shadow-sm rounded-bl-md',
          streaming && 'opacity-90',
        )}
      >
        <span className="whitespace-pre-wrap break-words">{m.corpo}</span>
        {m.ts && (
          <span
            className={cn(
              'block text-right text-[0.6rem] leading-none mt-1',
              isUser ? 'text-white/70' : 'text-text-muted',
            )}
          >
            {fmtHoraMsg(m.ts)}
          </span>
        )}
      </div>
    </Linha>
  );
}

const WAVE = [30, 55, 80, 45, 95, 60, 35, 70, 90, 50, 25, 65, 85, 40, 75, 55, 92, 45, 30, 70, 60, 85, 50, 35, 75, 88, 40, 55];

function AudioBubble({ src, durMs, isUser }: { src: string; durMs?: number | undefined; isUser: boolean }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  }
  const dur =
    durMs != null && durMs > 0
      ? `${Math.floor(durMs / 60000)}:${String(Math.floor((durMs % 60000) / 1000)).padStart(2, '0')}`
      : '';
  return (
    <div
      className={cn(
        'flex max-w-[78%] items-center gap-2.5 rounded-2xl px-3 py-2.5',
        isUser ? 'bg-[var(--accent)] rounded-br-md' : 'bg-surface shadow-sm rounded-bl-md',
      )}
    >
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full',
          isUser ? 'bg-white text-accent' : 'bg-[var(--accent)] text-white',
        )}
        aria-label={playing ? 'Pausar' : 'Tocar'}
      >
        {playing ? <Pause size={16} strokeWidth={2} /> : <Play size={16} strokeWidth={2} className="ml-0.5" />}
      </button>
      <div className="flex h-6 flex-1 items-center gap-[2px]">
        {WAVE.map((h, i) => (
          <span
            key={i}
            className={cn('w-[3px] rounded-full', isUser ? 'bg-white/70' : 'bg-[var(--accent-300)]')}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <span className={cn('shrink-0 text-[0.68rem] tabular-nums', isUser ? 'text-white/85' : 'text-text-2')}>
        {dur}
      </span>
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}
